/**
 * Duenner Fetch-Wrapper fuer die Worker-API.
 *
 * - Haengt den Stack-Auth-Access-Token als Bearer an (nie direkter DB-Zugriff vom Browser).
 * - Im Dev wird der Tenant-Slug als X-Maestro-Tenant-Header mitgeschickt, weil localhost
 *   keine Mandanten-Subdomain hat. In Produktion traegt die Subdomain den Mandanten und
 *   VITE_DEV_TENANT ist NICHT gesetzt.
 *
 * OFFENER PUNKT: exakte Methode zum Auslesen des Tokens (user.getAuthJson()). Siehe README.
 */
import { stackApp } from "../stack";

const API_URL = import.meta.env.VITE_API_URL ?? "";
const DEV_TENANT = import.meta.env.VITE_DEV_TENANT;

// BUGHUNT-2026-08-25 (Kundenliste-Reload haengt ~20s hinter dem Copilot): jeder apiFetch()-Aufruf
// im gesamten Frontend loeste bisher UNABHAENGIG stackApp.getUser()+user.getAuthJson() aus - ohne
// Caching/Deduplizierung. Feuern mehrere Aufrufer nahezu gleichzeitig (z. B. die Copilot-Spalte
// mit befunde/briefing/agent-message UND ein voellig unabhaengiger Seiten-Reload), serialisiert
// sich das zu N unabhaengigen, offenbar seriell abgearbeiteten SDK-Aufloesungen - der spaeteste
// Aufrufer wartet dann auf ALLE davor. Single-Flight-Caching behebt genau das: der erste Aufrufer
// stoesst die Aufloesung an, alle waehrenddessen ankommenden Aufrufer haengen sich an DIESELBE
// Promise, statt eine eigene SDK-Anfrage zu starten - sobald die eine Aufloesung durch ist,
// dispatchen alle wartenden fetch()-Calls sofort. Bewusst NUR der In-Flight-Zustand wird geteilt
// (kein TTL/Cache des Tokens selbst): sobald die Promise settled, wird `inFlightAccessToken`
// wieder geleert (auch im Fehlerfall) - der naechste Aufruf loest frisch auf, exakt wie vorher,
// nur eben nicht mehr N-fach parallel/seriell.
let inFlightAccessToken: Promise<string | null> | null = null;

function resolveAccessToken(): Promise<string | null> {
  if (inFlightAccessToken) return inFlightAccessToken;
  inFlightAccessToken = (async () => {
    try {
      const user = await stackApp.getUser();
      if (!user) throw Object.assign(new Error("Nicht eingeloggt"), { statusCode: 401 });
      // { accessToken, refreshToken } - wir brauchen den accessToken (JWT) fuer den Worker.
      const { accessToken } = await user.getAuthJson();
      return accessToken;
    } finally {
      inFlightAccessToken = null;
    }
  })();
  return inFlightAccessToken;
}

async function buildHeaders(extra?: HeadersInit): Promise<Headers> {
  const accessToken = await resolveAccessToken();

  const headers = new Headers(extra);
  headers.set("Authorization", `Bearer ${accessToken}`);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (DEV_TENANT) headers.set("X-Maestro-Tenant", DEV_TENANT);
  return headers;
}

/** Wartezeiten vor dem 2. und 3. Versuch eines lesenden Abrufs (Netzfehler, 28.09.2026). */
export const LESE_WIEDERHOLUNG_MS = [300, 1000];

/**
 * Lesende Abrufe (GET/HEAD) wiederholen einen reinen Netzfehler (`fetch` wirft, z. B. „Failed to
 * fetch“ beim Aufwachen des Tabs oder im Deploy-Wechsel) bis zu zweimal mit kurzem Backoff.
 * Schreibende Methoden nie — ein POST, dessen Antwort verloren ging, kann schon gewirkt haben.
 * HTTP-Fehlerantworten (4xx/5xx) werden nicht wiederholt; offline auch nicht.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = await buildHeaders(init.headers);
  const lesend = ["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase());
  for (let versuch = 0; ; versuch++) {
    try {
      return await fetch(`${API_URL}${path}`, { ...init, headers });
    } catch (e) {
      const nochmal =
        lesend && e instanceof TypeError && versuch < LESE_WIEDERHOLUNG_MS.length && navigator.onLine !== false && !init.signal?.aborted;
      if (!nochmal) throw e;
      await new Promise((r) => setTimeout(r, LESE_WIEDERHOLUNG_MS[versuch]));
    }
  }
}

export type ApiError = Error & { statusCode: number; code?: string; body?: unknown };

/**
 * Auffangnetz, wenn der Server zu einem Fehlercode KEINEN deutschen Text mitschickt (Antoine,
 * 03.11.2026: Betreiber sahen „Etwas ist schiefgelaufen · validation_error“). Der Server setzt die
 * `message` fuer Validierungsfehler inzwischen zentral (app.ts); das hier faengt Altstaende, andere
 * Aufrufer und 500er ab, damit nie ein nackter Code im Fenster steht. Unbekannte Codes bleiben
 * unveraendert (Rueckgabe der Code selbst), der Code steht weiter in `ApiError.code`.
 */
export function klartextFuerCode(b: { error?: string; issues?: unknown }): string | undefined {
  if (b.error === "validation_error") {
    const erste = Array.isArray(b.issues) ? (b.issues[0] as { path?: unknown } | undefined) : undefined;
    const pfad = Array.isArray(erste?.path) ? (erste!.path as Array<string | number>) : [];
    const feld = [...pfad].reverse().find((p) => typeof p === "string");
    return feld
      ? `Die Eingabe wurde nicht gespeichert: Bitte die Angabe „${feld}“ prüfen.`
      : "Die Eingabe wurde nicht gespeichert: Bitte die Angaben prüfen.";
  }
  if (b.error === "internal_error") return "Auf dem Server ist ein Fehler aufgetreten. Bitte noch einmal versuchen.";
  return b.error;
}

/** Wandelt eine nicht-OK-Response in einen Fehler mit statusCode (fuer Refine authProvider.onError).
 *  `code` traegt den rohen maschinenlesbaren Fehlercode (body.error, z. B. "empty_catalog"), sofern
 *  vorhanden - zusaetzlich zur menschenlesbaren `message`. Slice 3 (Fehler-in-den-Chat, §6.4) nutzt
 *  `code`, um den Rueckkanal reportContext() strukturiert zu fuettern statt auf den Anzeigetext zu
 *  regex-en. Rein additiv, bricht bestehende `.message`-Nutzung nicht. */
export async function toApiError(res: Response): Promise<ApiError> {
  let message = res.statusText;
  let code: string | undefined;
  let body: unknown;
  try {
    const b = (await res.json()) as { message?: string; error?: string; issues?: unknown };
    body = b;
    code = b.error;
    message = b.message ?? klartextFuerCode(b) ?? message;
  } catch {
    /* ignore */
  }
  // `body`: der volle Fehler-Umschlag (Punkt 94: `lage` bei `kostenuebernahme_entscheidung_fehlt`).
  return Object.assign(new Error(message), { statusCode: res.status, code, body });
}
